/**
 * Text (design: dashboard-v7 #chatView), running on today's chat engine:
 *   MessagingProvider/useMessaging (src/legacy/features/messaging/MessagingContext.tsx) = socket + REST fallback +
 *   5 s poll when the socket is down + reconnect catch-up + optimistic sends with tempId + read receipts + typing.
 * Paid items, freebies, gift cards, images/videos/voice render with today's AttachmentDisplay (and its purchase window).
 * Tip / Buzz (= paid priority message) / voice note / GIF open today's windows.
 */
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { MessagingProvider, useMessaging } from '@/features/messaging'
import { AttachmentDisplay } from '@/features/messaging/AttachmentDisplay'
import { TipAmountModal } from '@/features/messaging/TipAmountModal'
import { TipPaymentModal } from '@/features/messaging/TipPaymentModal'
import { PriorityPaymentModal } from '@/features/messaging/PriorityPaymentModal'
import VoiceRecordModal from '@/features/messaging/components/VoiceRecordModal'
import { GifPicker, type GifAttachment } from '@/features/messaging/gif'
import { FEATURES } from '~/config/mode'
import { api } from '~/api/client'
import { showcaseVip } from '~/showcase/samples'
import { vip } from '~/api/endpoints'
import { Icon, longTime, shortTime, useShowModal, useToast } from '~/app/ui'
import { usePin } from '~/app/pin'

export function Text() {
  const { isUnlocked, ask } = usePin()
  const navigate = useNavigate()
  const ok = isUnlocked()
  useEffect(() => { if (!ok) { navigate('/home', { replace: true }); ask('/text') } }, [ok])
  if (!ok) return null
  return <MessagingProvider><Chat /></MessagingProvider>
}

const HEART = '❤️'
/** The design's six gifts: [sprite icon, label, emoji in the gift note] */
const GIFTS: [string, string, string][] = [['s-flower', 'Flowers', '💐'], ['s-spark', 'Sparkle', '✨'], ['i-heart', 'Heart', '💗'], ['s-sun', 'Sunshine', '☀️'], ['s-leaf', 'Leaf', '🍂'], ['i-lipstick', 'Lipstick', '💄']]
const GAP_MS = 15 * 60 * 1000

function Chat() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const m = useMessaging()
  const [text, setText] = useState('')
  const [plus, setPlus] = useState(false)
  const [sheet, setSheet] = useState<null | 'buzz' | 'gift'>(null)
  const [win, setWin] = useState<null | 'gif' | 'voice' | 'tipAmount' | 'tipPay' | 'priority'>(null)
  const [tipAmount, setTipAmount] = useState(50)
  const [buzzText, setBuzzText] = useState('')
  const [gift, setGift] = useState('Flowers')
  const [selected, setSelected] = useState<string | null>(null)
  const [isVip, setIsVip] = useState(false)
  const list = useRef<HTMLUListElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const sending = useRef(false)
  const olderFrom = useRef(0)
  const lastId = useRef<string | null>(null)
  const creator = m.conversation?.creator
  const creatorId = m.conversation?.creator_id || ''
  const convId = m.conversation?.id || ''

  useEffect(() => { vip.isVip().then((v) => setIsVip(v || showcaseVip(user?.email))).catch(() => setIsVip(showcaseVip(user?.email))) }, [user?.email])

  // Reply to a gallery photo: /text?replyToGalleryImage=<id> (the gallery viewer's "Text me")
  useEffect(() => {
    const id = params.get('replyToGalleryImage')
    if (!id) return
    api<any>(`/api/gallery/images/${id}/preview`).then((r) => {
      if (r.data?.data) { m.setReplyingToGalleryImage(r.data.data); params.delete('replyToGalleryImage'); setParams(params, { replace: true }); input.current?.focus() }
    })
  }, [params])

  // Keep the newest message in view; keep position when older messages load at the top
  useLayoutEffect(() => {
    const el = list.current
    if (!el) return
    if (olderFrom.current) { el.scrollTop += el.scrollHeight - olderFrom.current; olderFrom.current = 0; return }
    const lastMsg = m.messages[m.messages.length - 1]
    const last = lastMsg?.id || null
    if (last !== lastId.current) {
      // first load, her own message, or she is following the newest -> go to the bottom; if she is reading older texts,
      // a new message from Caryn does not yank her down
      if (lastId.current === null || pinned.current || lastMsg?.sender_id === user?.id) { pinned.current = true; toBottom(el) }
      lastId.current = last
    }
  }, [m.messages])
  useEffect(() => { if (m.adminIsTyping && list.current && pinned.current && !userBusy()) toBottom(list.current) }, [m.adminIsTyping])
  // Following the newest message (owner 11-04): Text opens at the bottom and keeps the newest message in view while
  // photos / voice notes finish loading - but it NEVER fights the fan's finger. It stops following the moment she touches,
  // drags or wheels the list (and while the scroll is still gliding), and follows again only once she is back at the very
  // bottom by herself, or sends a message. (Before: a 120px "near the bottom" zone + re-snapping on every size change;
  // today's paid-photo card re-renders every second and the phone's browser bar resizes the view while scrolling, so
  // scrolling up from a paid photo kept snapping back down - "it locks the screen".)
  const pinned = useRef(true)
  const touching = useRef(false)
  const lastUserScroll = useRef(0)
  const programmatic = useRef(false)
  const userBusy = () => touching.current || Date.now() - lastUserScroll.current < 800
  const toBottom = (el: HTMLElement) => { programmatic.current = true; el.scrollTop = el.scrollHeight }
  // owner 11-04 (fans: "can't see what we type"): on phones the keyboard covers a full-height chat - size the chat to the
  // VISIBLE area (visualViewport) so the typing box sits right above the keyboard, and keep the newest message in view
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const root = document.documentElement
    let lastH = 0
    let settle: ReturnType<typeof setTimeout> | undefined
    let fullH = Math.round(vv.height)
    const set = () => {
      root.style.setProperty('--cw-vvh', `${Math.round(vv.height)}px`)
      root.style.setProperty('--cw-vvtop', `${Math.round(vv.offsetTop)}px`)
      // keyboard open = the visible area shrank by more than 120px: compact header / note / reply bar (owner 11-04: with the
      // keyboard up the chat chrome left almost no room to see the conversation and what you type)
      fullH = Math.max(fullH, Math.round(vv.height))
      root.classList.toggle('cw-kb-open', fullH - vv.height > 120)
      // only when the visible HEIGHT changes (keyboard opening/closing) - not on every scroll of the page or browser bar
      const h = Math.round(vv.height)
      if (h !== lastH) {
        lastH = h
        if (pinned.current && !userBusy() && list.current) toBottom(list.current)
        // the keyboard slides in/out over ~300ms: once it has settled, make sure the newest message is fully in view
        // (only if she is still following the bottom and not touching the chat)
        clearTimeout(settle)
        settle = setTimeout(() => { if (pinned.current && !userBusy() && list.current) toBottom(list.current) }, 350)
      }
    }
    set(); vv.addEventListener('resize', set); vv.addEventListener('scroll', set)
    return () => { vv.removeEventListener('resize', set); vv.removeEventListener('scroll', set); root.style.removeProperty('--cw-vvh'); root.style.removeProperty('--cw-vvtop'); root.classList.remove('cw-kb-open'); clearTimeout(settle) }
  }, [])
  useEffect(() => {
    const el = list.current
    if (!el) return
    const stick = () => { if (pinned.current && !userBusy()) toBottom(el) }
    // her hand on the list = she is in control: stop following until she is back at the bottom
    // a TAP (to reply, open a photo...) is not scrolling: only an actual scroll away from the bottom stops following
    // (owner 11-04: after tapping a message to reply, the keyboard opened over Caryn's newest message)
    const down = () => { touching.current = true }
    const up = () => { touching.current = false }
    const wheel = () => { lastUserScroll.current = Date.now() }
    el.addEventListener('touchstart', down, { passive: true })
    el.addEventListener('touchend', up, { passive: true })
    el.addEventListener('touchcancel', up, { passive: true })
    el.addEventListener('mousedown', down) // dragging the scrollbar on a computer
    window.addEventListener('mouseup', up)
    el.addEventListener('wheel', wheel, { passive: true })
    el.addEventListener('load', stick, true)
    const ro = new ResizeObserver(stick)
    for (const c of Array.from(el.children)) ro.observe(c)
    const mo = new MutationObserver(() => { for (const c of Array.from(el.children)) ro.observe(c); stick() })
    mo.observe(el, { childList: true })
    stick()
    return () => {
      el.removeEventListener('touchstart', down); el.removeEventListener('touchend', up); el.removeEventListener('touchcancel', up)
      el.removeEventListener('mousedown', down); window.removeEventListener('mouseup', up); el.removeEventListener('wheel', wheel)
      el.removeEventListener('load', stick, true); ro.disconnect(); mo.disconnect()
    }
  }, [])
  const onScroll = () => {
    const el = list.current
    if (!el) return
    if (programmatic.current) programmatic.current = false
    else lastUserScroll.current = Date.now() // her own scroll, incl. the glide after lifting the finger
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 4
    if (!atBottom && userBusy()) pinned.current = false
    if (atBottom && !touching.current) pinned.current = true // back at the very bottom by herself: follow the newest again
    if (el.scrollTop < 40 && m.hasMoreMessages && !olderFrom.current) { olderFrom.current = el.scrollHeight; m.loadMoreMessages() }
  }

  async function send(e?: FormEvent) {
    e?.preventDefault()
    const content = text.trim()
    if (!content || sending.current) return
    sending.current = true
    setText('')
    m.notifyStoppedTyping()
    input.current?.focus() // keep the phone keyboard up
    try { await m.sendMessage(content, [], m.replyingTo?.id, m.replyingToGalleryImage?.id) }
    catch { setText((t) => t || content) } // put it back so nothing typed is lost
    finally { sending.current = false }
  }
  async function sendGif(g: GifAttachment) {
    setWin(null)
    try { await m.sendMessage('', [g], m.replyingTo?.id, m.replyingToGalleryImage?.id) } catch { /* error strip shows it */ }
  }

  const myId = user?.id
  const lastMine = [...m.messages].reverse().find((x) => x.sender_id === myId)

  return (
    <section className="chat" aria-label={`Texts with ${creator?.username || 'Caryn'}`}>
      <div className="chat-col">
        <header className="chat-head">
          <button className="icon-btn" id="chatBack" type="button" aria-label="Back to home" onClick={() => navigate('/home')}><Icon id="i-back" /></button>
          <div className="chat-id">
            <div className="avatar avatar-photo" aria-hidden="true">{creator?.avatar_url ? <img src={creator.avatar_url} alt="" width={230} height={230} /> : <span>C</span>}</div>
            <h2>{creator?.username || 'Caryn'}</h2>
            {m.isTyping && <span className="sr-only">typing</span>}
          </div>
          {isVip ? <span className="vip"><Icon id="i-heart" />VIP</span> : <span aria-hidden="true" />}
        </header>
        <p className="sheet-note"><Icon id="i-lock" /> Confidential. Just between you and me.</p>
        {m.error && m.messages.length > 0 && (
          <p className="sheet-note v2-error" role="alert">{m.error}</p>
        )}

        <ul className="messages" ref={list} onScroll={onScroll} aria-label="Messages" aria-live="polite">
          {m.isLoading && m.messages.length === 0 && (
            // owner 11-04: a quick skeleton while the chat loads
            <li className="v2-skel-chat" aria-label="Loading your texts" role="status">
              {['them w60', 'them w40', 'me w55', 'them w70', 'me w35', 'them w50'].map((c, i) => <span key={i} className={`v2-skel v2-skel-bubble ${c}`} />)}
            </li>
          )}
          {m.isLoading && m.messages.length > 0 && <li className="event">Loading your texts…</li>}
          {!m.isLoading && m.error && m.messages.length === 0 && (
            <li className="event">Your texts didn’t load. <button className="link-btn" type="button" onClick={() => window.location.reload()}>Try again</button></li>
          )}
          {!m.isLoading && !m.error && m.messages.length === 0 && <li className="event">Say hi, I’d love to hear from you.</li>}
          {m.hasMoreMessages && !m.isLoading && <li className="event"><button className="link-btn" type="button" onClick={() => { olderFrom.current = list.current?.scrollHeight || 0; m.loadMoreMessages() }}>Earlier texts</button></li>}

          {m.messages.map((msg, i) => {
            const mine = msg.sender_id === myId
            const prev = m.messages[i - 1]
            const gap = !prev || new Date(msg.created_at).getTime() - new Date(prev.created_at).getTime() > GAP_MS
            const body = (msg.content || '').trim()
            const hasText = !!body && body !== '[Attachment]'
            const atts = msg.attachments || []
            const onlyMedia = atts.length > 0 && !hasText
            const myReaction = msg.reactions?.find((r: any) => r.user_id === myId)
            const theirReaction = msg.reactions?.find((r: any) => r.user_id !== myId)
            const paid = atts.some((a: any) => a.is_paid || a.product_id)
            return [
              gap ? <li key={`t-${msg.clientKey || msg.id}`} className="v2-stamp">{longTime(msg.created_at)}</li> : null,
              <li key={msg.clientKey || msg.id} id={`message-${msg.id}`}
                className={`bubble ${mine ? 'me' : 'them'}${(msg as any).is_priority ? ' v2-priority' : ''}${onlyMedia ? ' v2-media' : ''}${m.highlightedMessageId === msg.id ? ' v2-highlight' : ''}`}
                onClick={(e) => { if (!(e.target as HTMLElement).closest('button, a, video, audio, .tw-scope')) setSelected(selected === msg.id ? null : msg.id) }}>
                <span className="v2-bubble-body">
                  {(msg as any).is_priority && <span className="v2-priority-tag"><Icon id="i-buzz" />Buzzed</span>}
                  {msg.reply_to && (
                    <button type="button" className="v2-quote" onClick={() => m.scrollToMessage(msg.reply_to!.id)}>
                      {(msg.reply_to.content || '').trim() && msg.reply_to.content !== ' ' ? msg.reply_to.content : 'Photo'}
                    </button>
                  )}
                  {msg.reply_to_gallery_image && (
                    <span className="v2-quote v2-quote-img">{msg.reply_to_gallery_image.thumbnail_url && <img src={msg.reply_to_gallery_image.thumbnail_url} alt="" />}About this photo</span>
                  )}
                  {atts.length > 0 && (
                    <span className="tw-scope v2-atts"><AttachmentDisplay attachments={atts} isSentByMe={mine} recentlyUnlockedProducts={m.recentlyUnlockedProducts} /></span>
                  )}
                  {hasText && <span className="v2-text">{body}</span>}
                  {selected === msg.id && !paid && (
                    <span className="v2-actions"><button type="button" className="link-btn" onClick={() => { m.startReply(msg); setSelected(null); input.current?.focus() }}><Icon id="i-reply" />Reply</button></span>
                  )}
                </span>
                {/* hearts are the only reaction in v2: anything the team reacted with shows as a heart */}
                {FEATURES.hearts && theirReaction && <span className="v2-react" aria-label={`${creator?.username || 'Caryn'} loved this`}>♥</span>}
                {FEATURES.hearts && (
                  <button type="button" className="heart-btn" aria-pressed={!!myReaction} aria-label={myReaction ? 'Remove heart' : 'Heart this message'}
                    onClick={() => (myReaction && myReaction.emoji !== HEART ? m.toggleReaction(msg.id, myReaction.emoji) : m.toggleReaction(msg.id, HEART))}>
                    <Icon id="i-heart" />
                  </button>
                )}
              </li>,
              mine && lastMine?.id === msg.id && msg.read_at ? <li key={`r-${msg.id}`} className="v2-read">Read {shortTime(msg.read_at)}</li> : null,
            ]
          })}
          {m.adminIsTyping && <li className="bubble them v2-typing" aria-label="typing"><i /><i /><i /></li>}
        </ul>

        {(m.replyingTo || m.replyingToGalleryImage) && (
          <div className="v2-replying">
            {m.replyingToGalleryImage?.thumbnail_url && <img src={m.replyingToGalleryImage.thumbnail_url} alt="" />}
            <span>Replying to {m.replyingToGalleryImage ? 'a photo' : `“${(m.replyingTo?.content || 'a photo').trim().slice(0, 60)}”`}</span>
            <button type="button" className="icon-btn" aria-label="Cancel reply" onClick={() => { m.cancelReply(); m.setReplyingToGalleryImage(null) }}><Icon id="i-close" /></button>
          </div>
        )}

        <form className="chat-composer" onSubmit={send}>
          <div className="plus-menu" hidden={!plus}>
            <button type="button" onClick={() => { setPlus(false); setWin('tipAmount') }}><Icon id="i-tip" />Send tip</button>
            {FEATURES.gift && <button type="button" onClick={() => { setPlus(false); setSheet('gift') }}><Icon id="i-gift" />Send gift</button>}
            <button type="button" onClick={() => { setPlus(false); setBuzzText(text.trim()); setSheet('buzz') }}><Icon id="i-buzz" />Buzz Caryn’s phone</button>
          </div>
          <button className="cbtn gif-btn" type="button" aria-label="Send a GIF" onClick={() => { setPlus(false); setWin('gif') }}>GIF</button>
          <button className="cbtn" type="button" aria-haspopup="true" aria-expanded={plus} aria-label="More: tip, buzz" onClick={() => setPlus((p) => !p)}><Icon id="i-plus" /></button>
          <button className="cbtn" id="micBtn" type="button" aria-label="Record a voice message" onClick={() => { setPlus(false); setWin('voice') }}><Icon id="i-mic" /></button>
          <label className="sr-only" htmlFor="composeInput">Message</label>
          <input id="composeInput" ref={input} autoComplete="off" placeholder="Type your message" value={text} style={{ fontSize: 16 }}
            onChange={(e) => { setText(e.target.value); if (e.target.value) m.notifyTyping(); else m.notifyStoppedTyping() }}
            onBlur={() => m.notifyStoppedTyping()} />
          <button className="cbtn send" type="submit" aria-label="Send" disabled={!text.trim()}><Icon id="i-send" /></button>
        </form>
      </div>

      {sheet && (
        <ToolSheet title={sheet === 'buzz' ? 'Buzz Caryn’s phone' : 'Send a gift'} onClose={() => setSheet(null)}>
          {sheet === 'buzz' ? (
            <>
              <div className="buzz-phone" aria-hidden="true"><Icon id="i-buzz" /></div>
              <p style={{ textAlign: 'center' }}>Your message goes straight to her phone as a priority text. It’s a paid message; you’ll see the price next.</p>
              <label className="sr-only" htmlFor="buzzText">Your message</label>
              <textarea id="buzzText" className="v2-buzz-input" rows={3} placeholder="Write what you want her to see" value={buzzText} onChange={(e) => setBuzzText(e.target.value)} />
              <button className="btn" type="button" disabled={!buzzText.trim()} onClick={() => { setSheet(null); setWin('priority') }}>Continue</button>
            </>
          ) : (
            <>
              <p>Choose a little something. It’s free and arrives in her texts.</p>
              <div className="gift-grid">
                {GIFTS.map(([icon, label]) => (
                  <button key={label} className="pick" type="button" aria-pressed={gift === label} onClick={() => setGift(label)}>
                    <svg viewBox="0 0 24 24"><use href={`#${icon}`} /></svg><small>{label}</small>
                  </button>
                ))}
              </div>
              <button className="btn" type="button" onClick={async () => {
                const g = GIFTS.find(([, l]) => l === gift)!
                setSheet(null)
                try { await m.sendMessage(`${g[2]} I sent you ${g[1].toLowerCase() === 'heart' ? 'a heart' : g[1].toLowerCase()} — a little gift for you`, []); toast('Gift sent 💗') } catch { /* error strip shows it */ }
              }}>Send gift</button>
              <p className="v2-muted" style={{ textAlign: 'center', marginTop: 10 }}>Test version: sent as a gift note in your texts.</p>
            </>
          )}
        </ToolSheet>
      )}

      <div className="tw-scope v2-chat-windows">
        <GifPicker isOpen={win === 'gif'} onClose={() => setWin(null)} onSelect={sendGif} userId={myId || 'anonymous'} />
        <VoiceRecordModal isOpen={win === 'voice'} onClose={() => setWin(null)} />
        <TipAmountModal isOpen={win === 'tipAmount'} onClose={() => setWin(null)} onConfirm={(a: number) => { setTipAmount(a); setWin('tipPay') }} />
        <TipPaymentModal isOpen={win === 'tipPay'} onClose={() => setWin(null)} onSuccess={() => { setWin(null); toast('Tip sent 💗') }}
          amount={tipAmount} recipientId={creatorId} conversationId={convId} />
        <PriorityPaymentModal isOpen={win === 'priority'} onClose={() => setWin(null)}
          onSuccess={() => { setWin(null); setBuzzText(''); setText(''); toast('Buzzed! Your message is on her phone.') }}
          recipientId={creatorId} messageContent={buzzText} conversationId={convId} />
      </div>
    </section>
  )
}

function ToolSheet({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null)
  useShowModal(ref)
  return (
    <dialog className="sheet" id="toolSheet" ref={ref} aria-label={title} onClose={onClose} onClick={(e) => { if (e.target === ref.current) ref.current?.close() }}>
      <div className="sheet-head"><h2>{title}</h2><button className="icon-btn" type="button" aria-label="Close" onClick={() => ref.current?.close()}><Icon id="i-close" /></button></div>
      <div className="tool-body">{children}</div>
    </dialog>
  )
}
